// Bundle-savings engine — CLIENT mirror of functions/_lib/pricing.ts.
// The live tiers come from /api/public/meta, so the only thing that must stay
// in sync is the pure compute function below (kept identical to the server).
import type { BundleTier } from '../types';

export const DEFAULT_TIERS: BundleTier[] = [
  { min: 1, rate: 0 },
  { min: 2, rate: 0.05 },
  { min: 3, rate: 0.1 },
  { min: 4, rate: 0.15 },
];

export function rateForCount(count: number, tiers: BundleTier[]): number {
  let rate = 0;
  for (const t of tiers) if (count >= t.min) rate = t.rate;
  return rate;
}

export interface BundleLine {
  price_cents: number;
  discount_cents: number;
  final_cents: number;
}

export interface BundleResult {
  count: number;
  rate: number;
  lines: BundleLine[];
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  next?: { min: number; rate: number; items_away: number; extra_savings_cents: number };
}

export function computeBundle(prices: number[], tiers: BundleTier[] = DEFAULT_TIERS): BundleResult {
  const count = prices.length;
  const rate = rateForCount(count, tiers);
  const lines = prices.map((p) => {
    const discount = Math.round(p * rate);
    return { price_cents: p, discount_cents: discount, final_cents: p - discount };
  });
  const subtotal = prices.reduce((s, p) => s + p, 0);
  const discount = lines.reduce((s, l) => s + l.discount_cents, 0);

  let next: BundleResult['next'];
  const higher = tiers.filter((t) => t.min > count && t.rate > rate).sort((a, b) => a.min - b.min)[0];
  if (higher) {
    next = {
      min: higher.min,
      rate: higher.rate,
      items_away: higher.min - count,
      extra_savings_cents: Math.round(subtotal * (higher.rate - rate)),
    };
  }

  return {
    count, rate, lines,
    subtotal_cents: subtotal,
    discount_cents: discount,
    total_cents: subtotal - discount,
    next,
  };
}

export function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}
